import { useState } from "react";
import { createHighlight, updateHighlight, deleteHighlight, getPlatformInfo, detectPlatform, getEmbedUrl, getThumbnailUrl } from "../services/highlightsService";
import { VscAdd, VscTrash, VscEdit, VscCheck, VscClose, VscPlay } from "react-icons/vsc";
import "./HighlightsManager.css";

function HighlightsManager({ userId, highlights, onHighlightsChange }) {
    const [isAdding, setIsAdding] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [newUrl, setNewUrl] = useState("");
    const [newTitle, setNewTitle] = useState("");
    const [editUrl, setEditUrl] = useState("");
    const [editTitle, setEditTitle] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [activeVideo, setActiveVideo] = useState(null);

    const MAX_HIGHLIGHTS = 10;

    const handleAdd = async () => {
        if (!newUrl.trim()) {
            setError("Please enter a video URL");
            return;
        }

        setSaving(true);
        setError("");

        const { data, error: createError } = await createHighlight(userId, {
            url: newUrl.trim(),
            title: newTitle.trim() || null,
            display_order: highlights.length
        });

        if (createError) {
            setError("Failed to add highlight");
            console.error(createError);
        } else {
            onHighlightsChange([...highlights, data]);
            setNewUrl("");
            setNewTitle("");
            setIsAdding(false);
        }

        setSaving(false);
    };

    const handleEdit = (highlight) => {
        setEditingId(highlight.id);
        setEditUrl(highlight.url);
        setEditTitle(highlight.title || "");
    };

    const handleSaveEdit = async (highlightId) => {
        if (!editUrl.trim()) {
            setError("Please enter a video URL");
            return;
        }

        setSaving(true);
        setError("");

        const { data, error: updateError } = await updateHighlight(highlightId, {
            url: editUrl.trim(),
            title: editTitle.trim() || null
        });

        if (updateError) {
            setError("Failed to update highlight");
            console.error(updateError);
        } else {
            onHighlightsChange(highlights.map(h => h.id === highlightId ? data : h));
            setEditingId(null);
        }

        setSaving(false);
    };

    const handleDelete = async (highlightId) => {
        if (!window.confirm("Remove this highlight?")) return;

        const { error: deleteError } = await deleteHighlight(highlightId);

        if (deleteError) {
            setError("Failed to delete highlight");
            console.error(deleteError);
        } else {
            onHighlightsChange(highlights.filter(h => h.id !== highlightId));
        }
    };

    const cancelAdd = () => {
        setIsAdding(false);
        setNewUrl("");
        setNewTitle("");
        setError("");
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditUrl("");
        setEditTitle("");
        setError("");
    };

    const openVideo = (highlight) => {
        const embedUrl = getEmbedUrl(highlight.url, highlight.platform);
        if (embedUrl) {
            setActiveVideo({ ...highlight, embedUrl });
        } else {
            window.open(highlight.url, "_blank", "noopener,noreferrer");
        }
    };

    const closeVideo = () => {
        setActiveVideo(null);
    };

    return (
        <div className="highlights-manager">
            <div className="highlights-header">
                <h3>Highlights</h3>
                {!isAdding && highlights.length < MAX_HIGHLIGHTS && (
                    <button className="btn-add-highlight" onClick={() => setIsAdding(true)}>
                        <VscAdd /> Add Video
                    </button>
                )}
            </div>

            {error && <div className="highlights-error">{error}</div>}

            {/* Add new highlight form */}
            {isAdding && (
                <div className="highlight-form">
                    <input
                        type="url"
                        placeholder="Paste video URL (YouTube, Vimeo, Instagram, TikTok...)"
                        value={newUrl}
                        onChange={(e) => setNewUrl(e.target.value)}
                        className="highlight-input"
                    />
                    <input
                        type="text"
                        placeholder="Title (optional) - e.g., 'U17 Final Goal'"
                        value={newTitle}
                        onChange={(e) => setNewTitle(e.target.value)}
                        className="highlight-input"
                        maxLength={100}
                    />
                    {newUrl && (
                        <div className="highlight-preview-badge">
                            Platform detected: <strong>{getPlatformInfo(detectPlatform(newUrl)).label}</strong>
                        </div>
                    )}
                    <div className="highlight-form-actions">
                        <button className="btn-save" onClick={handleAdd} disabled={saving}>
                            <VscCheck /> {saving ? "Adding..." : "Add"}
                        </button>
                        <button className="btn-cancel" onClick={cancelAdd} disabled={saving}>
                            <VscClose /> Cancel
                        </button>
                    </div>
                </div>
            )}

            {/* Highlights grid */}
            {highlights.length > 0 && !isAdding && (
                <div className="highlights-grid">
                    {highlights.map((highlight) => {
                        const platformInfo = getPlatformInfo(highlight.platform);
                        const thumbnail = getThumbnailUrl(highlight.url, highlight.platform);
                        const isEditing = editingId === highlight.id;

                        if (isEditing) {
                            return (
                                <div key={highlight.id} className="highlight-card highlight-edit-card">
                                    <div className="highlight-edit-form">
                                        <input
                                            type="url"
                                            value={editUrl}
                                            onChange={(e) => setEditUrl(e.target.value)}
                                            className="highlight-input"
                                            placeholder="Video URL"
                                        />
                                        <input
                                            type="text"
                                            value={editTitle}
                                            onChange={(e) => setEditTitle(e.target.value)}
                                            className="highlight-input"
                                            placeholder="Title (optional)"
                                            maxLength={100}
                                        />
                                        <div className="highlight-form-actions">
                                            <button className="btn-save" onClick={() => handleSaveEdit(highlight.id)} disabled={saving}>
                                                <VscCheck /> Save
                                            </button>
                                            <button className="btn-cancel" onClick={cancelEdit} disabled={saving}>
                                                <VscClose /> Cancel
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        }

                        return (
                            <div key={highlight.id} className="highlight-card">
                                <div 
                                    className="highlight-thumbnail"
                                    style={thumbnail ? { backgroundImage: `url(${thumbnail})` } : {}}
                                    onClick={() => openVideo(highlight)}
                                >
                                    <div 
                                        className="highlight-platform-icon"
                                        style={{ backgroundColor: platformInfo.color }}
                                    >
                                        {platformInfo.icon}
                                    </div>
                                    <div className="highlight-play-overlay">
                                        <VscPlay size={40} />
                                    </div>
                                </div>
                                <div className="highlight-card-footer">
                                    <span className="highlight-card-title">
                                        {highlight.title || platformInfo.label + " Video"}
                                    </span>
                                    <div className="highlight-card-actions">
                                        <button className="btn-icon" onClick={() => handleEdit(highlight)} title="Edit">
                                            <VscEdit />
                                        </button>
                                        <button className="btn-icon btn-danger" onClick={() => handleDelete(highlight.id)} title="Delete">
                                            <VscTrash />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {highlights.length === 0 && !isAdding && (
                <p className="highlights-empty">No highlights yet. Add your best moments!</p>
            )}

            {/* Video modal */}
            {activeVideo && (
                <div className="video-modal" onClick={closeVideo}>
                    <div className="video-modal-content" onClick={(e) => e.stopPropagation()}>
                        <button className="video-modal-close" onClick={closeVideo}>
                            <VscClose size={24} />
                        </button>
                        <div className="video-embed-wrapper">
                            <iframe
                                src={activeVideo.embedUrl}
                                title={activeVideo.title || "Highlight video"}
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                            />
                        </div>
                    </div>
                </div>
            )}

            {highlights.length >= MAX_HIGHLIGHTS && (
                <p className="highlights-limit">Maximum {MAX_HIGHLIGHTS} highlights allowed</p>
            )}
        </div>
    );
}

export default HighlightsManager;
